import { ColDef } from "ag-grid-community";

import { filterComparators, formatters, getters } from "lib/agGrid";
import { caseInsensitiveComparator } from "lib/agGrid/columnDefinitions";
import { datetimeFormatter } from "lib/agGrid/columns/datetime";
import { GeneralEditRenderer } from "lib/agGrid/columns/edit";
import {
  searchableStringQuickFilterTextGetter,
  stringValueGetter,
} from "lib/agGrid/columns/searchableString";
import { floatGetter } from "lib/agGrid/getters";
import { booleanRenderer } from "lib/agGrid/renderers";
import { AggridColDefContext } from "lib/agGrid/types";

import { ColumnType } from "./constants";

// The valueGetter functions sourced from JS lib files have narrower parameter types than
// ValueGetterFunc requires (they destructure `colDef.field` as required). Cast to satisfy
// ag-grid's columnTypes prop; runtime behaviour is correct.
export const GlobalColumnTypes = {
  [ColumnType.STRING]: {
    valueGetter: stringValueGetter,
    getQuickFilterText: searchableStringQuickFilterTextGetter,
  },

  [ColumnType.NUMERIC]: {
    filter: "agNumberColumnFilter",
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
    enableRowGroup: true,
    enableValue: true,
  },
  [ColumnType.PERCENTAGE]: {
    valueGetter: getters.percentageGetter,
    valueFormatter: formatters.percentageFormatter,
    filter: "agNumberColumnFilter",
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
    enableValue: true,
  },
  [ColumnType.UTC_LOCAL_DATE_TIME]: {
    valueFormatter: datetimeFormatter,
    filter: "agDateColumnFilter",
    filterParams: {
      comparator: filterComparators.localDateComparator,
      // TODO: Handle datetimes better and allow time based filtering.
      includeTime: false,
    },
  },
  [ColumnType.DATE_TIME]: {
    filter: "agDateColumnFilter",
    filterParams: {
      comparator: filterComparators.localDateComparator,
      // TODO: Handle datetimes better and allow time based filtering.
      includeTime: false,
    },
    valueGetter: getters.dateGetter,
    valueFormatter: formatters.localDateTimeFormatter,
  },
  [ColumnType.BOOLEAN]: {
    cellRenderer: booleanRenderer,
    filter: "agSetColumnFilter",
    filterParams: {
      values: [true, false],
    },
  },
  [ColumnType.TOTAL]: {
    enableValue: true,
    hide: true,
    pinned: true,
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
  },
  [ColumnType.GROUPER]: {
    enableRowGroup: true,
    rowGroup: false,
    valueGetter: getters.groupingKeyGetter,
    comparator: caseInsensitiveComparator,
  },
  [ColumnType.RIGHT_ALIGNED]: {
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
  },
  [ColumnType.EDIT]: {
    cellRenderer: GeneralEditRenderer,
    width: 100,
  },
  [ColumnType.DATE]: {
    filter: "agDateColumnFilter",
    filterParams: { comparator: filterComparators.localDateComparator },
    valueGetter: getters.dateGetter,
    valueFormatter: formatters.localDateFormatter,
  },

  [ColumnType.CURRENCY_FROM_CENTS]: {
    filter: "agNumberColumnFilter",
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
    valueGetter: floatGetter,
    valueFormatter: formatters.floatFormatter,
    context: {
      valueGetterParams: {
        multiplier: 0.01,
      },
      valueFormatterParams: {
        decimalPlaces: 4,
        formatToMinTwoDecimalPlaces: true,
      },
    } satisfies AggridColDefContext,
    enableValue: true,
  },
  [ColumnType.CURRENCY]: {
    filter: "agNumberColumnFilter",
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
    valueGetter: floatGetter,
    valueFormatter: formatters.floatFormatter,
    context: {
      valueFormatterParams: {
        decimalPlaces: 2,
        formatToMinTwoDecimalPlaces: true,
      },
    } satisfies AggridColDefContext,
    enableValue: true,
  },

  [ColumnType.WEIGHT_FROM_GRAMS]: {
    filter: "agNumberColumnFilter",
    headerClass: "ag-right-aligned-header",
    cellClass: "ag-right-aligned-cell",
    valueGetter: floatGetter,
    valueFormatter: formatters.floatFormatter,
    context: {
      valueGetterParams: {
        multiplier: 0.001,
      },
      valueFormatterParams: {
        decimalPlaces: 4,
        formatToMinTwoDecimalPlaces: true,
      },
    } satisfies AggridColDefContext,
    enableValue: true,
  },
} as Record<string, ColDef>;
